import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import 'Modules/video/components/vm-video-comments-button/vm-video-comments-button';
import 'Modules/video/components/vm-video-save-menu/vm-video-save-menu';
import type { PlaylistOption } from 'Modules/video/components/vm-video-save-menu/vm-video-save-menu';
import 'Modules/video/components/vm-video-share-menu/vm-video-share-menu';
import 'Modules/video/components/vm-video-slides-button/vm-video-slides-button';
import 'Modules/video/components/vm-video-thumbs-up-button/vm-video-thumbs-up-button';
import Styles from './vm-video-actions.scss';

/**
 * Per-video action bar. Composes the thumbs-up button, comments button
 * (mobile only), share menu and save-to-playlist menu side-by-side. Each
 * child owns its own API/state; this wrapper is purely structural — it
 * forwards shared props down and hydrates the server-rendered playlist
 * list for the save menu.
 *
 * ## Playlist hydration
 *
 * Initial playlist data is provided as light-DOM seed elements rendered by
 * Django:
 *
 * ```html
 * <vm-video-actions ...>
 *   <div hidden class="vm-video-actions__playlist-seed"
 *        data-playlist-id="42"
 *        data-playlist-slug="my-playlist"
 *        data-playlist-title="My Playlist"></div>
 *   ...more seeds...
 * </vm-video-actions>
 * ```
 *
 * On `firstUpdated`, the wrapper reads all `.vm-video-actions__playlist-seed`
 * elements, parses their data attributes into a `PlaylistOption[]`, removes
 * them from light DOM, and passes the array to `vm-video-save-menu` via the
 * `initialPlaylists` property. From that point on, the save menu owns the
 * playlist list internally; the wrapper is no longer involved.
 *
 * Seeds added dynamically after `firstUpdated` are NOT hydrated — the read
 * is one-time. Don't rely on slot-style dynamic insertion for playlists.
 *
 * @example
 * ```html
 * <vm-video-actions
 *   video-slug="some-video"
 *   video-url="https://www.vumedi.com/video/some-video/"
 *   video-title="Some Video"
 *   thumbs-up-count="42"
 *   user-has-liked
 *   user-has-bookmarked
 *   user-profile-uuid="abc-123"
 * >
 *   <div hidden class="vm-video-actions__playlist-seed"
 *        data-playlist-id="1"
 *        data-playlist-slug="cardio-favorites"
 *        data-playlist-title="Cardio Favorites"></div>
 * </vm-video-actions>
 * ```
 */
@customElement('vm-video-actions')
export class VmVideoActions extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    @property({ type: Number, attribute: 'thumbs-up-count' })
    thumbsUpCount = 0;

    @property({ type: Boolean, attribute: 'user-has-liked' })
    userHasLiked = false;

    @property({ type: Boolean, attribute: 'user-has-bookmarked' })
    userHasBookmarked = false;

    @property({ type: Boolean, attribute: 'user-has-saved-to-playlist' })
    userHasSavedToPlaylist = false;

    @property({ type: String, attribute: 'user-profile-uuid' })
    userProfileUuid = '';

    @property({ type: String, attribute: 'video-url' })
    videoUrl = '';

    @property({ type: String, attribute: 'video-title' })
    videoTitle = '';

    @property({ type: Boolean, attribute: 'share-disabled' })
    shareDisabled = false;

    @property({ type: Boolean, attribute: 'comments-disabled' })
    commentsDisabled = false;

    @property({ type: Boolean, attribute: 'can-view-comments' })
    canViewComments = false;

    /** Renders the "View slides" button; the viewer itself is mounted by the page template. */
    @property({ type: Boolean, attribute: 'slides-available' })
    slidesAvailable = false;

    @state()
    private _initialPlaylists: PlaylistOption[] = [];

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-video-actions');
        this.dataset.testid = 'vm-video-actions';
    }

    override firstUpdated() {
        // Hydrate playlist data from server-rendered placeholder elements,
        // then drop them from light DOM. The save button consumes the array
        // via its `initialPlaylists` property and owns the list from there.
        const items = Array.from(this.querySelectorAll<HTMLElement>('.vm-video-actions__playlist-seed'));
        if (items.length === 0) return;
        this._initialPlaylists = items.map((el) => ({
            id: parseInt(el.dataset.playlistId ?? '', 10),
            slug: el.dataset.playlistSlug ?? '',
            title: el.dataset.playlistTitle ?? '',
        }));
        items.forEach((el) => el.remove());
    }

    override render() {
        return html`
            ${this.slidesAvailable
                ? html`<vm-video-slides-button video-slug=${this.videoSlug}></vm-video-slides-button>`
                : nothing}
            <vm-video-thumbs-up-button
                video-slug=${this.videoSlug}
                thumbs-up-count=${this.thumbsUpCount}
                ?user-has-liked=${this.userHasLiked}
            ></vm-video-thumbs-up-button>
            ${this.commentsDisabled
                ? nothing
                : html`<vm-video-comments-button
                      video-slug=${this.videoSlug}
                      ?can-view-comments=${this.canViewComments}
                  ></vm-video-comments-button>`}
            ${this.shareDisabled
                ? nothing
                : html`
                      <vm-video-share-menu
                          video-slug=${this.videoSlug}
                          video-url=${this.videoUrl}
                          video-title=${this.videoTitle}
                      ></vm-video-share-menu>
                  `}
            <vm-video-save-menu
                video-slug=${this.videoSlug}
                user-profile-uuid=${this.userProfileUuid}
                ?user-has-bookmarked=${this.userHasBookmarked}
                ?user-has-saved-to-playlist=${this.userHasSavedToPlaylist}
                .initialPlaylists=${this._initialPlaylists}
            ></vm-video-save-menu>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-actions': VmVideoActions;
    }
}
